import React, { useState, useEffect } from 'react';
import { UserJourney } from '@/types/journey';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, X, List } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from '@/hooks/use-mobile';

interface JourneyGoalProps {
  scenario: string;
  goal: string;
  expectations: string[];
  onUpdate: (update: Partial<UserJourney>) => void;
  isComplete?: boolean;
  isMobile?: boolean;
}

// Component for editing scenario with cancel/save buttons
const EditableScenario = ({
  value,
  onChange,
  isMobile
}: {
  value: string;
  onChange: (value: string) => void;
  isMobile: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  
  useEffect(() => {
    setTempValue(value);
  }, [value]);
  
  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };
  
  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditing) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSave();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, tempValue, value, onChange]);
  
  if (isEditing) {
    return (
      <div className="space-y-1">
        <label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Scenario</label>
        <div className="relative">
          <Textarea 
            placeholder="E.g., User is researching and comparing different project management tools for their design team..." 
            value={tempValue} 
            rows={2}
            className="whitespace-pre-wrap overflow-wrap-break-word text-sm"
            onChange={(e) => setTempValue(e.target.value)}
            autoFocus
          />
          {tempValue && (
            <button 
              type="button"
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              onClick={() => setTempValue('')}
              aria-label="Clear scenario"
            >
              <X className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
            </button>
          )}
          <div className="flex justify-end space-x-2 mt-1">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancel}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={handleSave}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h4 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium mb-1`}>Scenario</h4>
      <div 
        className={`${value ? 'text-foreground' : 'text-muted-foreground italic'} ${isMobile ? 'text-xs' : 'text-sm'} cursor-pointer p-1.5 rounded hover:bg-muted/20 whitespace-pre-wrap`}
        onClick={() => setIsEditing(true)}
      >
        {value || "Click to add scenario..."}
      </div>
    </div>
  );
};

// Component for editing goal with cancel/save buttons
const EditableGoal = ({
  value,
  onChange,
  isMobile
}: {
  value: string;
  onChange: (value: string) => void;
  isMobile: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  
  useEffect(() => {
    setTempValue(value);
  }, [value]);
  
  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };
  
  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditing) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSave();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, tempValue, value, onChange]);
  
  if (isEditing) {
    return (
      <div className="space-y-1">
        <label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Goal</label>
        <div className="relative">
          <Textarea 
            placeholder="E.g., Find and implement a tool that lets them track design projects from concept to delivery..." 
            value={tempValue} 
            rows={2}
            className="whitespace-pre-wrap overflow-wrap-break-word text-sm"
            onChange={(e) => setTempValue(e.target.value)}
            autoFocus
          />
          {tempValue && (
            <button 
              type="button"
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              onClick={() => setTempValue('')}
              aria-label="Clear goal"
            >
              <X className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
            </button>
          )}
          <div className="flex justify-end space-x-2 mt-1">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancel}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={handleSave}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h4 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium mb-1`}>Goal</h4>
      <div 
        className={`${value ? 'text-foreground' : 'text-muted-foreground italic'} ${isMobile ? 'text-xs' : 'text-sm'} cursor-pointer p-1.5 rounded hover:bg-muted/20 whitespace-pre-wrap`}
        onClick={() => setIsEditing(true)}
      >
        {value || "Click to add goal..."}
      </div>
    </div>
  );
};

// Component for editing expectations (multiple items)
const EditableExpectations = ({
  values,
  onChange,
  isMobile
}: {
  values: string[];
  onChange: (values: string[]) => void;
  isMobile: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValues, setTempValues] = useState<string[]>(values);
  const [newExpectation, setNewExpectation] = useState('');
  
  useEffect(() => {
    setTempValues(values);
  }, [values]);
  
  const handleCancel = () => {
    setTempValues(values);
    setIsEditing(false);
    setNewExpectation('');
  };
  
  const handleSave = () => {
    onChange(tempValues);
    setIsEditing(false);
    setNewExpectation('');
  };
  
  const handleAddExpectation = () => {
    if (newExpectation.trim()) {
      setTempValues([...tempValues, newExpectation.trim()]);
      setNewExpectation('');
    }
  };
  
  const handleRemoveExpectation = (index: number) => {
    setTempValues(tempValues.filter((_, i) => i !== index));
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddExpectation();
    }
    
    // Command+S (Mac) or Ctrl+S (Windows)
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    
    // Escape key to cancel
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  
  if (isEditing) {
    return (
      <div className="space-y-1">
        <label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Expectations</label>
        <div className="space-y-2">
          {/* List existing expectations */}
          {tempValues.length > 0 && (
            <ul className="space-y-1">
              {tempValues.map((expectation, index) => (
                <li key={index} className="flex items-center">
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} flex-1 mr-2`}>{expectation}</span>
                  <button 
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => handleRemoveExpectation(index)}
                    aria-label="Remove expectation"
                  >
                    <X className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          
          {/* Add new expectation */}
          <div className="relative">
            <Textarea 
              placeholder="E.g., Find solutions that integrate with existing software..." 
              value={newExpectation} 
              rows={1}
              className="whitespace-pre-wrap overflow-wrap-break-word text-sm pr-8"
              onChange={(e) => setNewExpectation(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {newExpectation && (
              <button 
                type="button"
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                onClick={() => setNewExpectation('')}
                aria-label="Clear input"
              >
                <X className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
              </button>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 mt-1">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancel}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={handleAddExpectation}
              className={isMobile ? "text-xs h-7 px-2" : ""}
              disabled={!newExpectation.trim()}
            >
              Add
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={handleSave}
              className={isMobile ? "text-xs h-7 px-2" : ""}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h4 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium mb-1`}>Expectations</h4>
      <div 
        className={`cursor-pointer p-1.5 rounded hover:bg-muted/20`}
        onClick={() => setIsEditing(true)}
      >
        {tempValues.length > 0 ? (
          <ul className="list-disc pl-4 space-y-1">
            {tempValues.map((expectation, index) => (
              <li key={index} className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
                {expectation}
              </li>
            ))}
          </ul>
        ) : (
          <div className={`text-muted-foreground italic ${isMobile ? 'text-xs' : 'text-sm'}`}>
            Click to add user expectations...
          </div>
        )}
      </div>
    </div>
  );
};

const JourneyGoal: React.FC<JourneyGoalProps> = ({ 
  scenario, 
  goal, 
  expectations = [],
  onUpdate,
  isComplete = false,
  isMobile = false
}) => {
  const _isMobile = useIsMobile() || isMobile;
  
  const handleScenarioChange = (newScenario: string) => {
    onUpdate({ scenario: newScenario });
  };
  
  const handleGoalChange = (newGoal: string) => {
    onUpdate({ goal: newGoal });
  };
  
  const handleExpectationsChange = (newExpectations: string[]) => {
    onUpdate({ expectations: newExpectations });
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <Card className="shadow-md relative h-full">
          <CardHeader className="py-2 px-4">
            <h3 className="text-base font-medium">Journey Context</h3>
          </CardHeader>
          <CardContent className="px-4 py-2 space-y-3">
            <EditableScenario
              value={scenario}
              onChange={handleScenarioChange}
              isMobile={_isMobile}
            />
            
            <EditableGoal
              value={goal}
              onChange={handleGoalChange}
              isMobile={_isMobile}
            />
            
            <EditableExpectations
              values={expectations}
              onChange={handleExpectationsChange}
              isMobile={_isMobile}
            />
          </CardContent>
          
          <CheckCircle2 
            className={`h-4 w-4 absolute top-4 right-4 ${isComplete ? 'text-emerald-500' : 'text-gray-300'}`} 
            aria-label={isComplete ? "Journey context complete" : "Journey context incomplete"}
          />
        </Card>
        <TooltipContent>
          <p>Edit the scenario, goal, and expectations for this user journey</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default JourneyGoal;
